// Path: /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/pages/Partners.tsx
// Version: v1.0.1
// Description: Partner-Übersichtsseite mit alphabetischer Sortierung
import React, { useMemo } from "react";
import { useBreakpoints } from "../hooks/Breakpoints.tsx";
import Container from "../hooks/Container.tsx";

const partners = [
  "BlackRock (Netherlands) B.V.",
  "Bluerock Group AG",
  "BIM Berliner Immobilienmanagement GmbH",
  "CBRE GmbH",
  "Cushman & Wakefield acht+ GmbH",
  "HOWOGE Wärme GmbH",
  "Shore Capital International Ltd.",
  "Accentro GmbH",
  "JUNIQO Invest GmbH",
  "Accentro Wohneigentum GmbH",
  "DAN Bau GmbH",
  "LBJ-Berlin GmbH",
  "Synarchitects GmbH",
  "Quartier Hasenheide GmbH",
  "Brandenburg Properties 4 GP S.a.r.l.",
  "Kurfürstenstrasse 41-44 Grundstücks GmbH",
  "smava GmbH",
  "Aldaas GBR",
  "Bobbes Objekt X GmbH & Co. KG",
  "Bobbes Karl-Marx-Platz GmbH & Co. KG",
  "Lange Handels GmbH ",
  "Bobbes VIII GmbH & Co. KG",
  "Bobbes V GmbH & Co. KG",
  "Brandenburg Properties 8 GP S.a.r.l.",
  "Havel Living GmbH",
  "CBRE B.V.",
  "HOWOGE Wohnungsbaugesellschaft mbH",
  "HOWOGE Herbststraße GmbH",
  "HOWOGE High Deck Siedlung GmbH",
  "Immokomm 24 GmbH",
  "COPRO Projektentwicklung GmbH",
  "Densante GmbH",
  "C & W (U.K.) LLP German Branch",
  "Purpose Green Real Estate GmbH",
  "BIV Bingartes GmbH & Co. KG",
  "Land Berlin - Sondervermögen Immobilien des Landes Berlin (SILB)",
  "Accentro 11. Wohneigentum GmbH",
  "Riehmers Hofgarten Grundbesitz GmbH",
  "Accentro Real Estate AG",
  "Riehmers Dachgeschoss Grundbesitz GmbH",
  "KRAMER + KRAMER Bau- und Projektmanagement GmbH",
  "Koppenstraße 81 Dachgeschoss GmbH",
  "Berlinovo Immobilien GmbH",
  "Berlin Hyp AG",
  "Bodystreet GmbH",
  "fitbox GmbH",
  "Cushman & Wakefield GmbH",
  "RDC Deutschland GmbH",
  "Drylock Technologies GmbH",
  "Forum Soziale Dienste gGmbH",
  "Round Hill Capital Ventures Germany GmbH",
  "Deutsche Wohnen SE",
  "Adler Group S.A.",
  "GWH Immobilien Holding GmbH",
  "JOB POINT GesBiT",

  // unsortierte Liste – kann beliebig erweitert werden
];

type Group = { letter: string; names: string[] };

const Partners: React.FC = () => {
  /* ------------------------- Breakpoint-Flags ------------------------- */
  const { current } = useBreakpoints();
  const isMobile = current === "mobile";
  const isTablet = current === "tablet";
  const isDesktop = current === "desktop";

  /* --------------------- Gruppieren & Sortieren ----------------------- */
  const groups: Group[] = useMemo(() => {
    const sorted = [...partners].sort((a, b) =>
      a.localeCompare(b, "en", { sensitivity: "base" })
    );

    const map = new Map<string, string[]>();
    sorted.forEach((name) => {
      const letter = name[0]!.toUpperCase();
      if (!map.has(letter)) map.set(letter, []);
      map.get(letter)!.push(name);
    });

    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([letter, names]) => ({ letter, names }));
  }, []);

  /* --------------------- Typografie pro Breakpoint -------------------- */
  const titleSize = isDesktop ? "text-7xl" : isTablet ? "text-5xl" : "text-3xl";
  const letterSize = isDesktop ? "text-3xl" : isTablet ? "text-2xl" : "text-xl";
  const itemSize = isDesktop ? "text-lg" : isTablet ? "text-base" : "text-sm";

  /* ----------------------- Zwei-Spalten-Paare ------------------------- */
  const pairedGroups: Group[][] = [];
  if (!isMobile) {
    for (let i = 0; i < groups.length; i += 2) {
      pairedGroups.push(groups.slice(i, i + 2));
    }
  }

  /* ----------------------------- Render ------------------------------- */
  return (
    <section className="bg-black text-white pt-[50px] pb-[50px] sm:pt-[75px] sm:pb-[75px] lg:pt-[100px] lg:pb-[100px]">
      <Container>
        {/* Überschrift */}
        <h1 className={`font-bold mb-16 ${titleSize}`}>Our Partners</h1>

        {/* Mobile – eine Spalte */}
        {isMobile && (
          <div className="flex flex-col gap-y-12">
            {groups.map(({ letter, names }) => (
              <div key={letter}>
                <h2 className={`font-semibold ${letterSize}`}>{letter}</h2>
                <div className="h-px bg-white my-2" />
                <ul className="space-y-1">
                  {names.map((name) => (
                    <li key={name} className={itemSize}>
                      {name}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {/* Tablet & Desktop – zwei Spalten, alphabetisch von links nach rechts */}
        {!isMobile && (
          <div className="flex flex-col gap-y-12">
            {pairedGroups.map((pair, rowIdx) => (
              <div key={rowIdx} className="grid grid-cols-2 gap-x-12 gap-y-12">
                {pair.map(({ letter, names }) => (
                  <div key={letter}>
                    <h2 className={`font-semibold ${letterSize}`}>{letter}</h2>
                    <div className="h-px bg-white my-2" />
                    <ul className="space-y-1">
                      {names.map((name) => (
                        <li key={name} className={itemSize}>
                          {name}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {/* falls ungerade Gruppenanzahl → rechte Zelle leer */}
                {pair.length === 1 && <div />}
              </div>
            ))}
          </div>
        )}
      </Container>
    </section>
  );
};

export default Partners;
